import { isValidEmail } from "../../Shared/Utilities/string";
import { TelemetryProperties, trackAppInsightsEvent } from "../app-insights-telemetry";
import { oneTrustFieldValues, phoneSync } from "./field-capture";
import {
  getContactRequirement,
  getMissingContact,
  isValidOneTrustPhone
} from "./validity";

// Application Insights custom events for the client-side OneTrust consent capture, which until
// now only reported to the browser console - so "OneTrust received only a phone number" or
// "OneTrust has a consent but the CMS has no submission" couldn't be traced afterwards. Contains
// no personal data: only form/touchpoint identifiers, flags, counts and stable reason codes.
//
// Both events carry PageUrl (origin and path of the page the form is on - never the query string,
// which can carry personal data, e.g. a prefilled email address from a campaign link) and
// PreviousFailures (Failed/Blocked attempts for this form earlier on this page view - so a
// Captured/Succeeded event with PreviousFailures > 0 is a visitor who got past a failure, e.g. by
// correcting an email address the consent API rejected).
//
// OneTrustConsentCapture: one per submit attempt that reaches a capture decision.
//   Outcome: Captured | Failed | Skipped | Blocked
//   Reason (Failed/Skipped/Blocked): see CaptureReason
//   FormId (form container GUID - matches FormActorCompleted/Failed's FormId server-side),
//   Touchpoint, RequiredContact (EmailAndPhone | Email | Phone | EmailOrPhone - what the
//   touchpoint requires, see getContactRequirement), CollectionPoint, EmailFieldTagged,
//   PhoneFieldTagged, ContactSent (EmailAndPhone | EmailOnly | PhoneOnly | None), MissingContact
//   (Email | Phone | EmailAndPhone | EmailOrPhone - required contact details the payload lacks
//   or carries invalid; unset when RequiredContact is met), PhoneSync (NotApplicable | InSync | Waited |
//   TimedOut - whether PhoneInput.razor's tagged input had caught up with what the visitor typed,
//   see waitForPhoneInputSync), PhoneSyncWaitMs, PurposesSent, FailedValidators (FormsRejected:
//   the validators Forms is about to fail, e.g. OneTrustContactRequiredValidator), Prediction
//   (Accepted | Unknown), PredictionReason, DurationMs, CapturedAfterTimeout,
//   ErrorMessageShown (connection | email | phone | save | load), ErrorDetail (SDK/API error message,
//   with anything resembling an email address or phone number redacted).
//
// OneTrustFormSubmitted: the Forms Submit outcome for a form with a OneTrust touchpoint.
//   Outcome: Succeeded | ServerRejected | RequestFailed
//   FormId, CollectionPoint, IsFinalized, ConsentCaptured (whether consent has been captured for
//   this form on this page), SubmissionConsentCaptured (whether this submission's own consent
//   reached OneTrust - false for e.g. a "Next" post, or a capture skipped because the consent
//   element was hidden), ServerValidators (validators that failed server-side, e.g.
//   ReCaptchaValidator), HttpStatus.
//
// Example: consent captured, but the submission never succeeded on this page view
//   customEvents
//   | where name in ("OneTrustConsentCapture", "OneTrustFormSubmitted")
//   | extend FormId = tostring(customDimensions.FormId), Outcome = tostring(customDimensions.Outcome)
//   | summarize captured = countif(name == "OneTrustConsentCapture" and Outcome == "Captured"),
//               submitted = countif(name == "OneTrustFormSubmitted" and Outcome == "Succeeded")
//       by session_Id, FormId
//   | where captured > 0 and submitted == 0
//
// Example: consents that reached OneTrust without a contact detail their touchpoint requires, or
// whose phone number may have been read before PhoneInput.razor had caught up
//   customEvents
//   | where name == "OneTrustConsentCapture" and customDimensions.Outcome == "Captured"
//   | extend Required = tostring(customDimensions.RequiredContact),
//            Sent = tostring(customDimensions.ContactSent),
//            PhoneSync = tostring(customDimensions.PhoneSync)
//   | where (Required == "EmailAndPhone" and Sent != "EmailAndPhone") or PhoneSync == "TimedOut"
//   | summarize count() by tostring(customDimensions.Touchpoint), Required, Sent, PhoneSync
//
// Example (hypercare): forms a visitor couldn't get past OneTrust on - a Failed/Blocked attempt,
// and no successful submission of that form for the rest of the session - by page. A visitor who
// corrects their details and resubmits drops out. ErrorMessageShown tells the visitor's own errors
// (email | phone: the consent API rejected what they entered) from ours or OneTrust's (connection |
// save | load); open PageUrl to check the form.
//   customEvents
//   | where name in ("OneTrustConsentCapture", "OneTrustFormSubmitted")
//   | extend FormId = tostring(customDimensions.FormId), PageUrl = tostring(customDimensions.PageUrl),
//            Outcome = tostring(customDimensions.Outcome)
//   | summarize failures = countif(Outcome in ("Failed", "Blocked")),
//               succeeded = countif(name == "OneTrustFormSubmitted" and Outcome == "Succeeded"),
//               Reasons = make_set_if(tostring(customDimensions.Reason), Outcome in ("Failed", "Blocked")),
//               Shown = make_set(tostring(customDimensions.ErrorMessageShown)),
//               lastFailure = maxif(timestamp, Outcome in ("Failed", "Blocked"))
//       by session_Id, PageUrl, FormId
//   | where failures > 0 and succeeded == 0
//   | summarize sessions = dcount(session_Id), Reasons = make_set(Reasons), Shown = make_set(Shown),
//                 lastFailure = max(lastFailure)
//       by PageUrl, FormId
//   | order by sessions desc
export const CONSENT_CAPTURE_EVENT = "OneTrustConsentCapture";
export const FORM_SUBMITTED_EVENT = "OneTrustFormSubmitted";

export type CaptureOutcome = "Captured" | "Failed" | "Skipped" | "Blocked";

export type CaptureReason =
  // Skipped: Forms is about to reject the step itself (shows its own errors).
  | "FormsRejected"
  // Skipped: the consent element isn't validated by Forms (hidden by a field dependency).
  | "ConsentNotGiven"
  | "NoValidContact"
  // Skipped: this exact payload was already captured for this form on this page.
  | "AlreadyCaptured"
  // Skipped: this exact payload was still being sent by an earlier attempt that timed out, so
  // this attempt waited on that call (which reports its own Captured) instead of sending it again.
  | "JoinedInFlight"
  // Skipped: a submit of this form was still in progress (e.g. a double-click), so this one was
  // dropped - Forms doesn't disable the submit button until onBeforeSubmit has resolved.
  | "SubmitInProgress"
  // Failed: the capture call didn't settle within the timeout, or the SDK/API rejected it.
  | "Timeout"
  | "ApiError"
  // Blocked: the submit was blocked before any capture could be attempted. The *Timeout reasons
  // are an SDK init/touchpoint render still unsettled when the visitor submitted, after waiting
  // for it - the visitor can retry, and gets through once it has settled.
  | "SdkUnavailable"
  | "SdkInitFailed"
  | "SdkInitTimeout"
  | "InvalidPageLanguage"
  | "TouchpointRenderFailed"
  | "TouchpointRenderTimeout"
  // Blocked: our own submit handling threw something no other reason covers (a bug, or the SDK
  // breaking in a new way). The exception, with its stack, is also tracked.
  | "UnexpectedError";

export type CaptureTelemetry = {
  outcome: CaptureOutcome;
  reason?: CaptureReason;
  prediction?: "Accepted" | "Unknown";
  predictionReason?: string;
  durationMs?: number;
  // The capture call succeeded only after the timeout had already blocked the submit.
  capturedAfterTimeout?: boolean;
  // Which error message the visitor was shown (see ConsentErrorKind).
  errorMessageShown?: string;
  error?: unknown;
  // FormsRejected: the validators Forms is about to fail (see FormsSubmitPrediction).
  failedValidators?: string[];
  // What this attempt sends (see describePayload), when it gets that far.
  payload?: PayloadSummary;
};

export type PayloadSummary = {
  contactSent: "EmailAndPhone" | "EmailOnly" | "PhoneOnly" | "None";
  missingContact?: ReturnType<typeof getMissingContact>;
  purposesSent: number;
  phoneSync: typeof phoneSync;
};

const EMAIL_PATTERN = /[^\s@]+@[^\s@]+/g;
const NUMBER_PATTERN = /\+?\d[\d\s().-]{4,}\d/g;

// SDK errors carry the API's validation messages (e.g. "The user's email address is malformed"),
// which are worth keeping - but never let a value the visitor typed slip into telemetry.
const sanitizeErrorDetail = (error: unknown): string | undefined => {
  if (error === undefined) {
    return undefined;
  }

  const message = error instanceof Error ? error.message : String(error);

  return message
    .replace(EMAIL_PATTERN, "[email]")
    .replace(NUMBER_PATTERN, "[number]")
    .slice(0, 300);
};

const describeContactSent = (): PayloadSummary["contactSent"] => {
  const hasEmail = !!oneTrustFieldValues.get("emailId")?.value;
  const hasPhone = !!oneTrustFieldValues.get("phoneId")?.value;

  if (hasEmail && hasPhone) {
    return "EmailAndPhone";
  }

  if (hasEmail) {
    return "EmailOnly";
  }

  return hasPhone ? "PhoneOnly" : "None";
};

const countPurposesSent = (): number =>
  Array.from(oneTrustFieldValues.entries()).filter(
    ([sdkId, { isCheckbox, value }]) =>
      sdkId.startsWith("purposeId-") && isCheckbox && value === "true"
  ).length;

// Which of the contact details the touchpoint requires the payload lacks (or carries invalid),
// judged on the payload itself rather than the form - so it agrees with ContactSent.
const describeMissingContact = (
  marker: HTMLElement
): PayloadSummary["missingContact"] => {
  const email = oneTrustFieldValues.get("emailId")?.value ?? "";
  const phone = oneTrustFieldValues.get("phoneId")?.value ?? "";

  return getMissingContact(getContactRequirement(marker), {
    isEmailValid: !!email && isValidEmail(email),
    // Sent with the "+" field-capture adds for E.164.
    isPhoneValid: isValidOneTrustPhone(phone.replace(/^\+/, ""))
  });
};

// Summarises the payload oneTrustFieldValues currently holds. Taken when the attempt starts rather
// than when its event fires: a capture call's outcome arrives later, by which time a later submit
// attempt (or another OneTrust form on the page) may have refilled oneTrustFieldValues.
export const describePayload = (marker: HTMLElement): PayloadSummary => ({
  contactSent: describeContactSent(),
  missingContact: describeMissingContact(marker),
  purposesSent: countPurposesSent(),
  phoneSync
});

// Failed/Blocked capture attempts per form on this page view (see PreviousFailures).
const failuresByForm = new Map<string, number>();

const getPreviousFailures = (form: HTMLFormElement | null): number | undefined =>
  form ? (failuresByForm.get(form.id) ?? 0) : undefined;

// Failed/Blocked attempts reported for this form so far - compared before and after a submit
// attempt, to tell whether an error it threw has already been reported.
export const countReportedFailures = (form: HTMLFormElement): number =>
  failuresByForm.get(form.id) ?? 0;

// Origin and path only - see PageUrl.
const getPageUrl = (): string => `${window.location.origin}${window.location.pathname}`;

const describeForm = (form: HTMLFormElement | null): TelemetryProperties => {
  const marker = form?.querySelector<HTMLElement>("[data-ssocc-touchpoint]");

  return {
    PageUrl: getPageUrl(),
    PreviousFailures: getPreviousFailures(form),
    FormId: form?.id,
    Touchpoint: marker?.dataset.ssoccTouchpoint,
    RequiredContact: marker ? getContactRequirement(marker) : undefined,
    CollectionPoint: marker?.dataset.ssoccAnalyticsCollectionPoint,
    EmailFieldTagged: !!form?.querySelector('[data-ssocc-sdk-id="emailId"]'),
    PhoneFieldTagged: !!form?.querySelector('[data-ssocc-sdk-id="phoneId"]')
  };
};

export const trackConsentCapture = (
  form: HTMLFormElement | null,
  telemetry: CaptureTelemetry
): void => {
  trackAppInsightsEvent(CONSENT_CAPTURE_EVENT, {
    ...describeForm(form),
    Outcome: telemetry.outcome,
    Reason: telemetry.reason,
    Prediction: telemetry.prediction,
    PredictionReason: telemetry.predictionReason,
    DurationMs:
      telemetry.durationMs === undefined ? undefined : Math.round(telemetry.durationMs),
    CapturedAfterTimeout: telemetry.capturedAfterTimeout || undefined,
    ErrorMessageShown: telemetry.errorMessageShown,
    ErrorDetail: sanitizeErrorDetail(telemetry.error),
    ContactSent: telemetry.payload?.contactSent,
    MissingContact: telemetry.payload?.missingContact,
    PhoneSync: telemetry.payload?.phoneSync.status,
    PhoneSyncWaitMs: telemetry.payload?.phoneSync.waitedMs || undefined,
    PurposesSent: telemetry.payload?.purposesSent,
    FailedValidators: telemetry.failedValidators?.length
      ? telemetry.failedValidators.join(",")
      : undefined
  });

  if (form && (telemetry.outcome === "Failed" || telemetry.outcome === "Blocked")) {
    failuresByForm.set(form.id, (failuresByForm.get(form.id) ?? 0) + 1);
  }
};

export type FormSubmittedTelemetry = {
  outcome: "Succeeded" | "ServerRejected" | "RequestFailed";
  isFinalized?: boolean;
  consentCaptured: boolean;
  submissionConsentCaptured: boolean;
  serverValidators?: string[];
  httpStatus?: number;
};

export const trackFormSubmitted = (
  form: HTMLFormElement,
  telemetry: FormSubmittedTelemetry
): void => {
  const marker = form.querySelector<HTMLElement>("[data-ssocc-touchpoint]");

  trackAppInsightsEvent(FORM_SUBMITTED_EVENT, {
    PageUrl: getPageUrl(),
    PreviousFailures: getPreviousFailures(form),
    FormId: form.id,
    CollectionPoint: marker?.dataset.ssoccAnalyticsCollectionPoint,
    Outcome: telemetry.outcome,
    IsFinalized: telemetry.isFinalized,
    ConsentCaptured: telemetry.consentCaptured,
    SubmissionConsentCaptured: telemetry.submissionConsentCaptured,
    ServerValidators: telemetry.serverValidators?.length
      ? telemetry.serverValidators.join(",")
      : undefined,
    HttpStatus: telemetry.httpStatus
  });
};
